import React, { useEffect, useState } from "react";
import "./UserManagement.scss";
import { Button, Col, Flex, Menu, MenuProps, Row } from "antd";
import userIcon from "@assets/icons/user.svg";
import bellIcon from "@assets/icons/bell.svg";
import cardIcon from "@assets/icons/credit-card.svg";
import farm from "@assets/icons/farm.svg";
import chartBar from "@assets/icons/chart-bar.svg";
import addressIcon from "@assets/icons/location-marker (black).svg";
import watchlistIcon from "@assets/icons/watchlist.svg";
import settingIcon from "@assets/icons/setting.svg";
import receiptTaxIcon from "@assets/icons/receipt-tax.svg";
import PersonalInfo from "@components/PersonalInfo/PersonalInfo";
import FarmInfo from "@components/FarmInfo/FarmInfo";
import PaymentMethod from "@components/PaymentMethod/PaymentMethod";
import { useUser } from "contexts/UserContext";
import { IUserInfo } from "@utils/Interfaces";
import Password from "@components/Password/Password";
import AdvanceSecurity from "@components/AdvanceSecurity/AdvanceSecurity";
import { Link, useNavigate } from "react-router-dom";
import Localization from "@components/Localization/Localization";
import Helpers from "@utils/Helpers";
import Subscriptions from "@components/Subscriptions/Subscriptions";
import Notifications from "@components/Notifications/Notifications";
import Privacy from "@components/Privacy/Privacy";
import AccountClosure from "@components/AccountClosure/AccountClosure";
import Addresses from "@components/Addresses/Addresses";
import ads from "@assets/icons/ads.svg";
import AllAds from "@components/AllAds/AllAds";
import AdSlot from "@components/AdSlot/AdSlot";
import CreateAd from "@components/CreateAd/CreateAd";
import ReportGenerator from "@components/ReportGenerator/ReportGenerator";
import ProducerHub from "@containers/ProducerHub/ProducerHub";
import Watchlist from "@containers/Watchlist/Watchlist";
import {
  ArrowLeftOutlined,
  LeftCircleFilled,
  RightCircleFilled,
} from "@ant-design/icons";

type MenuItem = Required<MenuProps>["items"][number];

const UserManagement: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const direct = urlParams.get("direct");
  const [activeTab, setActiveTab] = useState("1");
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { userProfile } = useUser();
  const [user, setUser] = useState<IUserInfo>(userProfile!);
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (direct) {
      switch (direct) {
        case "personal-info":
          setActiveTab("1");
          break;
        case "farm":
          setActiveTab("2");
          break;
        case "setting":
          openKeys.push("9");
          setOpenKeys(openKeys);
          setActiveTab("9-1");
          break;
        case "report-generator":
          setActiveTab("10");
          break;
        case "ads":
          openKeys.push("4");
          setOpenKeys(openKeys);
          setActiveTab("4-1");
          break;
        case "ads-register":
          openKeys.push("4");
          setOpenKeys(openKeys);
          setActiveTab("4-1");
          break;
        case "add-slot":
          setActiveTab("4-3");
          break;
        case "payment":
          setActiveTab("5");
          break;
        case "address":
          setActiveTab("7");
          break;
        case "watchlist":
          setActiveTab("8");
          break;
      }
    }
  }, [direct]);

  const isAdsManagementVisible = false;
  const adsManagementMenuItem: MenuItem = {
    key: "4",
    icon: <img className="icon" src={ads} alt="icon" />,
    label: "Ads Management",
    children: [
      {
        key: "4-1",
        label: (
          <Link to="/profile?direct=ads" style={{ textDecoration: "none" }}>
            All Ads
          </Link>
        ),
      },
      {
        key: "4-2",
        label: "Analytics",
      },
      {
        key: "4-3",
        label: "Add Slot",
      },
    ],
  };

  const profileTabMenu: MenuItem[] = [
    {
      key: "1",
      icon: <img className="icon" src={userIcon} alt="icon" />,
      label: "Personal Info",
    },

    {
      key: "2",
      icon: (
        <img
          className={`icon ${
            !userProfile?.type_ids.includes(2) ? "disabled" : ""
          }`}
          src={farm}
          alt="icon"
        />
      ),
      label: "Farm Info",
      disabled: !userProfile?.type_ids.includes(2),
    },

    {
      key: "3",
      icon: (
        <img
          className={`icon ${
            !userProfile?.type_ids.includes(2) ? "disabled" : ""
          }`}
          src={chartBar}
          alt="icon"
        />
      ),
      label: "Producer Hub",
      disabled: !userProfile?.type_ids.includes(2),
    },
    {
      key: "10",
      icon: <img className="icon" src={receiptTaxIcon} alt="icon" />,
      label: "Receipt Tax",
    },
    ...(isAdsManagementVisible ? [adsManagementMenuItem] : []),
    {
      key: "5",
      icon: <img className="icon" src={cardIcon} alt="icon" />,
      label: "Payment Methods",
    },
    {
      key: "6",
      icon: <img className="icon" src={bellIcon} alt="icon" />,
      label: "Notifications",
    },
    {
      key: "7",
      icon: <img className="icon" src={addressIcon} alt="icon" />,
      label: (
        <Link to="/profile?direct=address" style={{ textDecoration: "none" }}>
          Addresses
        </Link>
      ),
    },
    {
      key: "8",
      icon: <img className="icon" src={watchlistIcon} alt="icon" />,
      label: (
        <Link
          to="/profile?direct=watchlist"
          style={{ textDecoration: "none" }}
        >
          Watchlist
        </Link>
      ),
    },
    {
      key: "9",
      icon: <img className="icon" src={settingIcon} alt="icon" />,
      label: "Settings",
      children: [
        {
          key: "9-1",
          label: "Currency and Units",
        },
        {
          key: "9-2",
          label: "Password",
        },
        {
          key: "9-3",
          label: "Advanced Security",
        },
        {
          key: "9-4",
          label: "Subcriptions",
        },
        {
          key: "9-5",
          label: "Notifications",
        },
        {
          key: "9-6",
          label: "Privacy",
        },
        {
          key: "9-7",
          label: "Account Closure",
        },
      ],
    },
  ];

  const onClick: MenuProps["onClick"] = (e) => {
    setActiveTab(e.key);
  };

  useEffect(() => {
    let interval: any;
    const key = "updatable";

    if (isUpdating) {
      Helpers.showUpdatingNotification(key);
    }

    return () => {
      clearInterval(interval);
      Helpers.closeNotification(key);
    };
  }, [isUpdating]);

  return (
    <div className="UserManagement">
      <div className="container">
        <Row>
          <Col span={isSidebarCollapsed ? 3 : 6}>
            <div className="collapse-side">
              <Menu
                onClick={onClick}
                inlineCollapsed={isSidebarCollapsed || screenWidth < 1200}
                style={{ maxWidth: 256 }}
                selectedKeys={[activeTab]}
                openKeys={openKeys}
                onOpenChange={(keys) => {
                  setOpenKeys(keys);
                }}
                mode="inline"
                items={profileTabMenu}
              />
              <Button
                className={`collapse-button ${
                  isSidebarCollapsed ? "collapsed" : ""
                }`}
                icon={
                  isSidebarCollapsed ? (
                    <RightCircleFilled style={{ fontSize: 20 }} />
                  ) : (
                    <LeftCircleFilled style={{ fontSize: 20 }} />
                  )
                }
                variant="link"
                color="default"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              />
            </div>
          </Col>
          <Col span={isSidebarCollapsed ? 21 : 18}>
            <div className="content-side">
              {activeTab === "1" && (
                <PersonalInfo
                  user={user}
                  setUser={setUser}
                  isUpdating={isUpdating}
                  setIsUpdating={setIsUpdating}
                />
              )}
              {activeTab === "2" && (
                <FarmInfo
                  user={user}
                  setUser={setUser}
                  isUpdating={isUpdating}
                  setIsUpdating={setIsUpdating}
                />
              )}
              {activeTab === "3" && <ProducerHub />}
              {activeTab === "4-1" && urlParams.get("direct") === "ads" && (
                <AllAds />
              )}
              {activeTab === "4-1" &&
                urlParams.get("direct") === "ads-register" && <CreateAd />}
              {activeTab === "4-3" && <AdSlot />}
              {activeTab === "5" && (
                <PaymentMethod
                  isUpdating={isUpdating}
                  setIsUpdating={setIsUpdating}
                />
              )}
              {activeTab === "7" && (
                <Addresses
                  isUpdating={isUpdating}
                  setIsUpdating={setIsUpdating}
                />
              )}
              {activeTab === "8" && <Watchlist />}

              {activeTab === "9-1" && (
                <Localization
                  isUpdating={isUpdating}
                  setIsUpdating={setIsUpdating}
                />
              )}
              {activeTab === "9-2" && (
                <Password setIsUpdating={setIsUpdating} />
              )}
              {activeTab === "9-3" && <AdvanceSecurity />}
              {activeTab === "9-4" && <Subscriptions />}
              {activeTab === "9-5" && <Notifications />}
              {activeTab === "9-6" && <Privacy />}
              {activeTab === "9-7" && <AccountClosure user={user} />}
              {activeTab === "10" && <ReportGenerator />}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default UserManagement;
